import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Container, Grid } from "@mui/material";
import { isEmpty, isNil } from "ramda";

import { appRoutes } from "routes";
import Faces from "components/Faces";
import ProductMetaTags from "components/ProductMetaTags";
import MorphablePackGroupPresenter from "presenters/MorphablePackGroupPresenter";
import MainTitle from "components/MainTitle";
import {
  useFaces,
  useSourcePhoto,
  usePeople,
  useLoading,
  useScroll,
  useScreen,
  useIndexScroll,
  useLastEcard,
  useRouter,
} from "hooks";
import { urlToBase64 } from "utils/imageUtils";
import { PHOTO_TYPES } from "enums";

import UpsellImage from "../UpsellImage";
import ContinueButton from "../ContinueButton";
import Title from "../Title";
import Description from "../Description";

const View = ({ morphablePackGroup, isMorphablePackGroupLoading }) => {
  const { people, totalPeopleCount, loadAdditionalPeople, peopleToUpdate, loadPeople } = usePeople();
  const { orphanFaces, totalFacesCount, loadAdditionalFaces, loadFaces, resetAdded } = useFaces();
  const { openModal: openSourcePhotoModal } = useSourcePhoto();
  const { setSourcePhoto } = useLastEcard();

  const SCROLL_POSITION_OFFSET = 750;
  const PER_PAGE = 50;
  const { isDesktop } = useScreen();
  const { replace } = useRouter();

  const { func: loadPeopleWithLoader, isNotFinished: isPeopleLoading } = useLoading(loadPeople);
  const { func: loadFacesWithLoader, isNotFinished: isFacesLoading } = useLoading(loadFaces);

  const isLoading = isPeopleLoading && isFacesLoading;
  const hasData = !isEmpty(people) || !isEmpty(orphanFaces);

  const { isMobile } = useScreen();
  const [disableContinueButton, setDisableContinueButton] = useState(true);
  const [isFaceChecked, setIsFaceChecked] = useState(false);
  const [loadedByScroll, setLoadedByScroll] = useState(false);
  const { handleScroll, scrollToEnd } = useScroll(!isDesktop, SCROLL_POSITION_OFFSET);

  useEffect(() => {
    loadPeopleWithLoader();
    loadFacesWithLoader();
  }, []);

  const handleLoadPeopleSuccess = (params) => {
    return loadAdditionalPeople(params);
  };

  const handleFaceSelected = async (face) => {
    const isFaceEmpty = isEmpty(face);
    setIsFaceChecked(!isFaceEmpty);

    if (isFaceEmpty) return;

    const head = face.type === "people" ? face.defaultHead : face;
    const id = head?.id;
    const asset = head?.asset;
    const base64Image = await urlToBase64(asset, "image/png");

    setSourcePhoto(
      {
        id,
        asset: base64Image,
        type: PHOTO_TYPES.HEAD,
      },
      morphablePackGroup,
    );
  };

  const handleLoadFacesSuccess = (params) => {
    return loadAdditionalFaces({
      ...params,
      ...{
        filter: {
          orphans: true,
        },
      },
    });
  };

  const updatedPeople = !isNil(people)
    ? people.map((person) => peopleToUpdate.find((personToUpdate) => person.id === personToUpdate.id) || person)
    : null;

  const {
    handleLoadData: handleLoadAdditionalPeople,
    hasMorePages: hasMorePeoplePages,
    page: currentPeoplePage,
  } = useIndexScroll(people, PER_PAGE, totalPeopleCount, handleLoadPeopleSuccess, updatedPeople);

  const {
    handleLoadData: handleLoadAdditionalFaces,
    hasMorePages: hasMoreFacesPages,
    page: currentFacesPage,
  } = useIndexScroll(orphanFaces, PER_PAGE, totalFacesCount, handleLoadFacesSuccess, orphanFaces);

  useEffect(() => {
    if (!isLoading) {
      if (scrollToEnd <= SCROLL_POSITION_OFFSET && !loadedByScroll) {
        setLoadedByScroll(true);
        if (hasMorePeoplePages || !currentPeoplePage) {
          handleLoadAdditionalPeople(handleLoadAdditionalFaces);
        }
        if (hasMoreFacesPages || !currentFacesPage) {
          handleLoadAdditionalFaces();
        }
      }
      if (scrollToEnd > SCROLL_POSITION_OFFSET) setLoadedByScroll(false);
    }
  }, [scrollToEnd, isLoading]);

  useEffect(() => {
    setDisableContinueButton(hasData && !isFaceChecked);
  }, [hasData, isFaceChecked]);

  const handleAddSourcePhoto = () => {
    resetAdded();
    openSourcePhotoModal();
  };

  const handleContinue = () => {
    if (!hasData) {
      handleAddSourcePhoto();
      return;
    }

    replace(appRoutes.syaiPhotoConfirmationPath());
  };

  return (
    <Container>
      <Grid container justifyContent="center" mt={2} mb={5} direction={isMobile ? "column" : "row"}>
        <Grid item container alignItems="center" justifyContent="center" xs={12} tablet={6}>
          <UpsellImage morphablePackGroup={morphablePackGroup} />
        </Grid>
        <Grid
          item
          container
          direction="column"
          justifyContent={isMobile ? "center" : "flex-start"}
          alignItems="center"
          textAlign="center"
          xs={12}
          tablet={6}
          pt={5}
        >
          <Title
            title={MorphablePackGroupPresenter.pageTitle(morphablePackGroup)}
            isLoading={isMorphablePackGroupLoading}
          />
          <Description
            description={MorphablePackGroupPresenter.description(morphablePackGroup)}
            isLoading={isMorphablePackGroupLoading}
          />
          {!isLoading && (
            <Faces
              people={people}
              faces={orphanFaces}
              isLoading={isLoading}
              onAddFace={handleAddSourcePhoto}
              onFaceSelected={handleFaceSelected}
              onScroll={handleScroll}
            />
          )}
          <Grid item container justifyContent="center" mt={4}>
            <ContinueButton loading={isLoading} disabled={disableContinueButton} onClick={handleContinue} />
          </Grid>
        </Grid>
      </Grid>
      {!isMorphablePackGroupLoading && (
        <>
          <MainTitle title={`${MorphablePackGroupPresenter.pageTitle(morphablePackGroup)} AI Photos`} />
          <ProductMetaTags product={morphablePackGroup} presenter={MorphablePackGroupPresenter} />
        </>
      )}
    </Container>
  );
};

View.propTypes = {
  morphablePackGroup: MorphablePackGroupPresenter.shape(),
  isMorphablePackGroupLoading: PropTypes.bool,
};

export default View;
